import React, { useState, useRef, useEffect } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  Chip,
  Grid,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Autocomplete } from '@react-google-maps/api';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Button, TextField, Typography } from '../../components/primitives';
import { FormSection } from '../../components/composed';

const RADIUS_OPTIONS = [1, 5, 10, 15, 20, 30, 50, 100];

const pickerTextFieldProps = {
  fullWidth: true,
  size: 'small',
};

const SearchComponent = ({
  primaryAddress,
  selectedAddress,
  addresses,
  onAddressSelect,
  onAddressChange,
  radius,
  setRadius,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  onSearch,
  isGoogleMapsLoaded,
  showTitle = true,
}) => {
  const { isAuthenticated } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [isPlacesReady, setIsPlacesReady] = useState(false);

  useEffect(() => {
    if (isGoogleMapsLoaded && window.google?.maps?.places) {
      setIsPlacesReady(true);
    } else {
      setIsPlacesReady(false);
    }
  }, [isGoogleMapsLoaded]);

  const formatAddressForDisplay = (address) => {
    if (!address) return '';

    const addressParts = [];
    if (address.address_1) {
      addressParts.push(address.address_1.trim());
    }
    if (address.city && address.state) {
      const cityState = address.zip_code
        ? `${address.city}, ${address.state}, ${address.zip_code}`
        : `${address.city}, ${address.state}`;
      addressParts.push(cityState);
    }

    const fullAddress = addressParts.join(' ');

    if (address.location_name) {
      return `${address.location_name} - ${fullAddress}`;
    }

    return fullAddress;
  };

  useEffect(() => {
    if (selectedAddress) {
      setInputValue(formatAddressForDisplay(selectedAddress));
    } else if (isAuthenticated && primaryAddress) {
      setInputValue(formatAddressForDisplay(primaryAddress));
    } else if (!isAuthenticated) {
      setInputValue('');
    }
  }, [selectedAddress, primaryAddress, isAuthenticated]);

  const handleInputFocus = (event) => {
    if (isAuthenticated && addresses && addresses.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleChipClick = (address, event) => {
    event?.preventDefault();
    event?.stopPropagation();
    onAddressSelect(address);
    setInputValue(formatAddressForDisplay(address));
    handlePopoverClose();
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    if (!place?.geometry?.location) return;

    const addressComponents = place.address_components || [];
    const getComponent = (type) => {
      const component = addressComponents.find((c) => c.types.includes(type));
      return component ? component.long_name : '';
    };

    const streetNumber = getComponent('street_number');
    const route = getComponent('route');
    const address_1 = [streetNumber, route].filter(Boolean).join(' ').trim();

    const addressData = {
      location_name: place.name || place.formatted_address || '',
      address_1: address_1 || place.formatted_address || '',
      city: getComponent('locality') || getComponent('administrative_area_level_2') || '',
      state: getComponent('administrative_area_level_1') || '',
      zip_code: getComponent('postal_code') || '',
      latitude: place.geometry.location.lat().toString(),
      longitude: place.geometry.location.lng().toString(),
    };

    setInputValue(formatAddressForDisplay(addressData) || place.formatted_address || '');

    if (onAddressChange) {
      onAddressChange(addressData);
    }
  };

  const open = Boolean(anchorEl);

  const addressField = isPlacesReady && !isAuthenticated ? (
    <Autocomplete
      onLoad={(autocomplete) => {
        autocompleteRef.current = autocomplete;
      }}
      onPlaceChanged={handlePlaceChanged}
      options={{
        types: ['address'],
      }}
    >
      <TextField
        fullWidth
        size="small"
        label="Searching from:"
        placeholder="Enter your address"
      />
    </Autocomplete>
  ) : (
    <>
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        label="Searching from:"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={handleInputFocus}
        placeholder={isAuthenticated ? 'Select an address' : 'Enter your address'}
      />
      {isAuthenticated && addresses && addresses.length > 0 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          disableRestoreFocus
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Box
            sx={{
              p: 2,
              minWidth: 300,
              maxWidth: 400,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                color: 'text.primary',
              }}
            >
              Saved Locations
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {addresses.map((address) => (
                <Chip
                  key={address.id}
                  label={formatAddressForDisplay(address)}
                  onClick={(e) => handleChipClick(address, e)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                  }}
                  color={
                    selectedAddress && selectedAddress.id === address.id
                      ? 'primary'
                      : 'default'
                  }
                  clickable
                  sx={{
                    justifyContent: 'flex-start',
                    height: 'auto',
                    py: 1,
                    '& .MuiChip-label': {
                      display: 'block',
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      textOverflow: 'clip',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%', p: 2, pt: showTitle ? 2 : 1 }}>
        <FormSection
          title={showTitle ? 'Search for events' : undefined}
          sx={{ mb: 0 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              {addressField}
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="search-within-label">Within</InputLabel>
                <Select
                  labelId="search-within-label"
                  label="Within"
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                >
                  {RADIUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option} {option === 1 ? 'mile' : 'miles'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: pickerTextFieldProps }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: pickerTextFieldProps }}
              />
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: { xs: 'none', sm: 'block' } }} />

            <Grid item xs={12} sm={4}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                slotProps={{ textField: pickerTextFieldProps }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                slotProps={{ textField: pickerTextFieldProps }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: 'flex', alignItems: 'stretch' }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={onSearch}
                fullWidth
                sx={{ height: '100%', minHeight: 40 }}
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </FormSection>
      </Box>
    </LocalizationProvider>
  );
};

export default SearchComponent;
